import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { MdOutlineArrowBack } from 'react-icons/md';
import GaugeComponent from 'react-gauge-component';
import moment from 'moment';
import axios from 'config/axios';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { setError } from 'store/error';
import ButtonLink from 'components/ButtonLink';
import LevelBar from 'components/LevelBar';
import useWindowSize from 'hooks/useWindowSize';
import { MULTIPLE_CHOICES } from 'global/constants';

import './index.css';

// New SHSAT form: 50 questions per section (ELA 50 + Math 50 = 100 total),
// 180 minutes total. Per-question time targets used for the time-spent
// coloring and the Section Time Averages gauges.
const timePerQuestionFull = 180 * 60 / 100;
const timePerQuestionOne = 90 * 60 / 50;

function DigitalSHSATReport() {
    const [testData, setTestData] = useState(null);
    const [sectionIndex, setSectionIndex] = useState(0);
    const [moduleIndex, setModuleIndex] = useState(0);
    const [linkCopied, setLinkCopied] = useState(false);
    // Which Level 1 domain's Level 2 breakdown is expanded (click to open),
    // mirroring the SAT report.
    const [rootCategoryName, setRootCategoryName] = useState('');

    const windowSize = useWindowSize();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { organization } = useAppSelector(state => state.organization);
    const { testId } = useParams();

    const formatTime = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = (hours && duration.minutes() < 10) ? `0${duration.minutes()}` : duration.minutes();
        const seconds = duration.seconds() < 10 ? `0${duration.seconds()}` : duration.seconds();

        return hours ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
    }

    const formatTime2 = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = duration.minutes();
        const seconds = duration.seconds();

        return hours 
            ? `${hours} hr ${minutes} min ${seconds} sec`
            : minutes
            ? `${minutes} min ${seconds} sec`
            : `${seconds} sec`;
    }

    const copyLink = () => {
        navigator.clipboard.writeText(window.location.href);

        setLinkCopied(true);
    }

    useEffect(() => {
        axios.get(`tests/digital/result/shsat/${testId}/`)
            .then(res => {
                setTestData(res.data);
            })
            .catch(error => {
                // console.log(error);
                dispatch(setError(error));
            });
    }, []);

    if (!testData) return <></>;

    return (
        <div className='layout'>
            <Helmet>
                <title>
                    {testData.name} Report - {organization.name}
                </title>
            </Helmet>
            <div className='digitalSHSATReport__bar'>
                <button
                    className='digitalSHSATReport__back'
                    onClick={() => navigate('/tests')}
                >
                    <MdOutlineArrowBack className='digitalSHSATReport__backIcon' />
                    Back to My Tests
                </button>
            </div>
            <div className='digitalSHSATReport__headerGroup'>
                <div className='digitalSHSATReport__headerSection'>
                    <img
                        className='digitalSHSATReport__headerLogo'
                        alt='logo'
                        src={testData.logo}
                    />
                </div>
                <div className='digitalSHSATReport__headerSection'>
                    <div className='digitalSHSATReport__headerInfo'>
                        <p>
                            Name: {testData.studentName}
                        </p>
                        <p>
                            Test name: {testData.name}
                        </p>
                        <p>
                            Tested on: {moment(testData.testDate).format('MMMM D YYYY')}
                        </p>
                    </div>
                </div>
            </div>
            <div className='digitalSHSATReport__headerGroup'>
                <h1 className='digitalSHSATReport__headerTitle'>
                    Test Result Overview
                </h1>
                <div className='digitalSHSATReport__buttonGroup'>
                    {/* <Button className='digitalSHSATReport__button' secondary onClick={handlePrint}>
                        PRINT REPORT
                    </Button> */}
                    <ButtonLink className='digitalSHSATReport__button' to={`/review/digital-shsat/${testId}`}>
                        REVIEW QUESTIONS
                    </ButtonLink>
                </div>
            </div>
            <div className='digitalSHSATReport__card'>
                <div className='digitalSHSATReport__cardRow'>
                    {/* Horizontal score band across the top: TOTAL SCORE +
                        per-section scores side by side. */}
                    <div className='digitalSHSATReport__scoresGroup'>
                        <div className='digitalSHSATReport__scoreBlock'>
                            <h2 className='digitalSHSATReport__scoreTitle'>TOTAL SCORE</h2>
                            {/* Headline is the RAW score (correct out of total).
                                Adaptive: rawCorrect; legacy: ``score`` is itself a
                                raw correct count. The scaled score is tutor-only
                                (behind a button on the tutor report). */}
                            <div className='digitalSHSATReport__scorePercent'>
                                {testData.isAdaptive ? (testData.rawCorrect ?? 0) : Math.round(Number(testData.score))}
                                <span style={{fontSize: '0.5em', fontWeight: 400}}>/{testData.totalQuestionCount}</span>
                            </div>
                        </div>
                        {testData.testSections.map((testSection, t) => (
                            <div className='digitalSHSATReport__scoreBlock' key={t}>
                                <h3 className='digitalSHSATReport__sectionScoreTitle'>
                                    {testSection.subject}
                                </h3>
                                <div className='digitalSHSATReport__scorePercent'>
                                    {testData.isAdaptive ? (testSection.rawCorrect ?? 0) : Math.round(Number(testSection.score))}
                                    <span style={{fontSize: '0.5em', fontWeight: 400}}>/{testSection.questionCount}</span>
                                </div>
                                <div style={{fontSize: 16, marginTop: 6, color: 'var(--gray500)'}}>
                                    Time: {formatTime(testData.testModules.filter(testModule => (
                                        testModule.subject === testSection.subject
                                    )).reduce((total, testModule) => (
                                        total + testModule.answers.reduce((time, answer) => time + answer.timeSpent, 0)
                                    ), 0))}
                                </div>
                            </div>
                        ))}
                    </div>
                    {/* Category Scores domain graphs — full width below the
                        score band. */}
                    <div className='digitalSHSATReport__overview'>
                        {testData.testSections.some(s => s.rootCategories && s.rootCategories.length > 0) && (<>
                            <h2 className='digitalSHSATReport__cardSubTitle'>
                                Category Scores
                            </h2>
                            <div className='digitalSHSATReport__overviewRow'>
                                {testData.testSections.map((testSection, s) => (
                                    <div className='digitalSHSATReport__overviewItem'>
                                        <h4 className='digitalSHSATReport__overviewLabel'>
                                            {testSection.subject}
                                        </h4>
                                        {testSection.rootCategories.map(rootCategory => (<>
                                            <h5
                                                className={rootCategoryName === rootCategory.name
                                                    ? 'digitalSHSATReport__levelLabel--active'
                                                    : 'digitalSHSATReport__levelLabel'
                                                }
                                                onClick={() => setRootCategoryName(rootCategoryName === rootCategory.name ? '' : rootCategory.name)}
                                            >
                                                {rootCategory.name}
                                                <div className={rootCategoryName === rootCategory.name
                                                    ? 'digitalSHSATReport__levelDetailed--active'
                                                    : 'digitalSHSATReport__levelDetailed'
                                                }>
                                                    {(rootCategory.categories ?? []).map(category => (
                                                        <div className='digitalSHSATReport__levelDetailedItem'>
                                                            <p>{category.name}</p>
                                                            <p className={category.numCorrect/category.numQuestions >= 0.8
                                                                ? 'digitalSHSATReport__levelDetailedGreen'
                                                                : category.numCorrect/category.numQuestions >= 0.4
                                                                ? 'digitalSHSATReport__levelDetailedYellow'
                                                                : 'digitalSHSATReport__levelDetailedRed'
                                                            }>
                                                                {category.numCorrect}/{category.numQuestions}
                                                            </p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </h5>
                                            <LevelBar
                                                className={s % 2 === 0 ? 'digitalSHSATReport__levelGreen' : 'digitalSHSATReport__levelOrange'}
                                                percent={rootCategory.numQuestions ? 100*rootCategory.numCorrect/rootCategory.numQuestions : 0}
                                                label={`${rootCategory.numCorrect}/${rootCategory.numQuestions}`}
                                            />
                                        </>))}
                                    </div>
                                ))}
                            </div>
                        </>)}
                    </div>
                </div>
                {testData.testSections.some(s => s.rootCategories && s.rootCategories.length > 0) && (<>
                {/* Grey divider + extra top spacing between Category Scores
                    and Section Time Averages. */}
                <div style={{ borderTop: '2px solid var(--gray300)', marginTop: 40 }} />
                <h2 className='digitalSHSATReport__cardSubTitle' style={{ marginTop: 30 }}>
                    Section Time Averages
                </h2>
                <div className='digitalSHSATReport__overviewRow'>
                    {testData.testSections.map(testSection => (
                        <div className='digitalSHSATReport__overviewItem'>
                            <h4 className='digitalSHSATReport__overviewLabel'>
                                {testSection.subject}
                            </h4>
                            <table className='digitalSHSATReport__timeTable'>
                                <tbody>
                                    <tr>
                                        <th></th>
                                        <th>Section Time</th>
                                        <th>Time Per Question</th>
                                    </tr>
                                    {testSection.rootCategories.map(rootCategory => (
                                        <tr>
                                            <td>
                                                {rootCategory.name}
                                            </td>
                                            <td>
                                                {formatTime2(rootCategory.timeSpent)}
                                            </td>
                                            <td>
                                                <GaugeComponent
                                                    className='digitalSHSATReport__timeGauge'
                                                    type='semicircle'
                                                    value={rootCategory.numQuestions ? rootCategory.timeSpent/rootCategory.numQuestions : 0}
                                                    minValue={0}
                                                    maxValue={timePerQuestionFull * 3}
                                                    arc={{
                                                        'subArcs': [
                                                            { length: 0.33, color: "#5BE12C"},
                                                            { length: 0.33, color: "#F5CD19"},
                                                            { length: 0.33, color: "#EA4228"}
                                                        ]
                                                    }}
                                                    pointer={{
                                                        'width': 10,
                                                    }}
                                                    labels={{
                                                        'valueLabel': {
                                                            'hide': true
                                                        },
                                                        'tickLabels': {
                                                            'hideMinMax': true
                                                        }
                                                    }}
                                                />
                                                <p>{formatTime2(rootCategory.numQuestions ? rootCategory.timeSpent/rootCategory.numQuestions : 0)}</p>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
                </>)}
            </div>
            <div className='digitalSHSATReport__questions'>
                <h2 className='digitalSHSATReport__questionsHeader'>
                    Questions Overview
                </h2>
                <div className='digitalSHSATReport__sectionTabs'>
                    {testData.testSections.map((testSection, t) => (
                        <button
                            className={sectionIndex === t
                                ? 'digitalSHSATReport__sectionTab--active'
                                : 'digitalSHSATReport__sectionTab'
                            }
                            onClick={() => {
                                setSectionIndex(t);
                                setModuleIndex(0);
                            }}
                            key={testSection.subject}
                        >
                            {testSection.subject}
                        </button>
                    ))}
                    <div className={sectionIndex === 0
                        ? 'digitalSHSATReport__sectionTabIndicatorLeft'
                        : 'digitalSHSATReport__sectionTabIndicatorRight'
                    }></div>
                </div>
                {testData.testModules.filter(testModule => (
                    testModule.subject === testData.testSections[sectionIndex].subject
                )).length > 1 &&
                    <div className='digitalSHSATReport__moduleTabs'>
                        {testData.testModules.filter(testModule => (
                            testModule.subject === testData.testSections[sectionIndex].subject
                        )).map((testModule, t) => (
                            <button
                                className={moduleIndex === t
                                    ? 'digitalSHSATReport__moduleTab--active'
                                    : 'digitalSHSATReport__moduleTab'
                                }
                                onClick={() => setModuleIndex(t)}
                                key={testModule.name}
                            >
                                Module {testModule.moduleNumber} {testModule.moduleNumber === 2 && (testModule.hard ? ' (Hard)' : ' (Easy)')}
                            </button>
                        ))}
                        <div className={moduleIndex === 0
                            ? 'digitalSHSATReport__moduleTabIndicatorLeft'
                            : 'digitalSHSATReport__moduleTabIndicatorRight'
                        }></div>
                    </div>
                }
                <div className='digitalSHSATReport__questionsOverview'>
                    <div className='digitalSHSATReport__questionsSummary'>
                        <div className='digitalSHSATReport__questionsSummaryItem'>
                            <p className='digitalSHSATReport__questionSummaryNumber'>
                                {testData.testModules.filter(testModule => (
                                    testModule.subject === testData.testSections[sectionIndex].subject
                                    && testModule.moduleNumber === moduleIndex + 1
                                )).reduce((total, testModule) => (
                                    total + testModule.answers.length
                                ), 0)}
                            </p>
                            <p className='digitalSHSATReport__questionSummaryText'>
                                Total Questions
                            </p>
                        </div>
                        <div className='digitalSHSATReport__questionsSummaryItem'>
                            <p className='digitalSHSATReport__questionSummaryNumber'>
                                {testData.testModules.filter(testModule => (
                                    testModule.subject === testData.testSections[sectionIndex].subject
                                    && testModule.moduleNumber === moduleIndex + 1
                                )).reduce((total, testModule) => (
                                    total + testModule.answers.filter(answer => answer.isCorrect).length
                                ), 0)}
                            </p>
                            <p className='digitalSHSATReport__questionSummaryText'>
                                Correct Answers
                            </p>
                        </div>
                        <div className='digitalSHSATReport__questionsSummaryItem'>
                            <p className='digitalSHSATReport__questionSummaryNumber'>
                                {testData.testModules.filter(testModule => (
                                    testModule.subject === testData.testSections[sectionIndex].subject
                                    && testModule.moduleNumber === moduleIndex + 1
                                )).reduce((total, testModule) => (
                                    total + testModule.answers.filter(answer => !answer.isCorrect).length
                                ), 0)}
                            </p>
                            <p className='digitalSHSATReport__questionSummaryText'>
                                Incorrect Answers
                            </p>
                        </div>
                    </div>
                    <table className='digitalSHSATReport__questionsTable'>
                        <tbody>
                            <tr>
                                <th>Question</th>
                                {organization.customizations.find(customization => customization === 'Enable Show Answer') && <th>Correct Answer</th>}
                                <th>Your Answer</th>
                                <th>Time Spent</th>
                            </tr>
                            {testData.testModules.filter(testModule => (
                                testModule.subject === testData.testSections[sectionIndex].subject
                            ))[moduleIndex].answers.map(answer => (
                                <tr key={testData.testModules[moduleIndex].name + answer.questionNumber}>
                                    <td>{answer.questionNumber}</td>
                                    {organization.customizations.find(customization => customization === 'Enable Show Answer') &&
                                        <td>
                                            {(() => {
                                                switch (answer.questionType) {
                                                case 1:
                                                    return answer.correctChoice !== null
                                                    ? MULTIPLE_CHOICES[answer.correctChoice]
                                                    : "-";
                                                case 3:
                                                    return answer.correctChoice?.length > 0
                                                    ? answer.correctChoice.map(i => MULTIPLE_CHOICES[i]).join(", ")
                                                    : "-";
                                                case 4:
                                                    return answer.correctAnswers?.length > 0
                                                    ? answer.correctAnswers.join(", ")
                                                    : "-";
                                                case 5:
                                                case 6:
                                                case 7:
                                                case 8:
                                                case 9:
                                                case 10:
                                                case 11:
                                                case 12:
                                                    return "See review";
                                                default:
                                                    return "-";
                                                }
                                            })()}
                                        </td>
                                    }
                                    <td>
                                        {(() => {
                                            switch (answer.questionType) {
                                            case 1:
                                                return answer.choice !== null ? (
                                                answer.choice === answer.correctChoice ? (
                                                    <p>{MULTIPLE_CHOICES[answer.choice]}; Correct</p>
                                                ) : (
                                                    <p className="digitalSHSATReport__questionIncorrect">
                                                    {MULTIPLE_CHOICES[answer.choice]}; Incorrect
                                                    </p>
                                                )
                                                ) : (
                                                <p className="digitalSHSATReport__questionOmitted">Omitted</p>
                                                );
                                            case 3:
                                                return answer?.choice?.length > 0? (
                                                JSON.stringify([...answer.choice].sort()) === JSON.stringify([...answer.correctChoice].sort()) ? (
                                                    <p>{answer.choice.map(i => MULTIPLE_CHOICES[i]).join(", ")}; Correct</p>
                                                ) : (
                                                    <p className="digitalSHSATReport__questionIncorrect">
                                                    {answer.choice.map(i => MULTIPLE_CHOICES[i]).join(", ")}; Incorrect
                                                    </p>
                                                )
                                                ) : (
                                                <p className="digitalSHSATReport__questionOmitted">Omitted</p>
                                                );
                                            case 4:
                                                return answer?.studentAnswers?.length > 0? (
                                                answer.correctAnswers.length === answer.studentAnswers.length &&
                                                answer.correctAnswers.flat().every(item => answer.studentAnswers.flat().includes(item)) ? (
                                                    <p>{answer.studentAnswers.join(", ")}; Correct</p>
                                                ) : (
                                                    <p className="digitalSHSATReport__questionIncorrect">
                                                    {answer.studentAnswers.join(", ")}; Incorrect
                                                    </p>
                                                )
                                                ) : (
                                                <p className="digitalSHSATReport__questionOmitted">Omitted</p>
                                                );
                                            case 5:
                                                return (answer.isCorrect
                                                    ? <p>Correct</p>
                                                    : answer.studentAnswers.length > 0
                                                    ? <p className="digitalSHSATReport__questionIncorrect">Incorrect</p>
                                                    : <p className="digitalSHSATReport__questionOmitted">Omitted</p>
                                                )
                                            case 6:
                                                return (answer.isCorrect
                                                    ? <p>Correct</p>
                                                    : answer.studentAnswers.length > 0
                                                    ? <p className="digitalSHSATReport__questionIncorrect">Incorrect</p>
                                                    : <p className="digitalSHSATReport__questionOmitted">Omitted</p>
                                                )
                                            case 7:
                                                const allEmpty =
                                                    Array.isArray(answer.studentAnswers) &&
                                                    answer.studentAnswers.length > 0 &&
                                                    answer.studentAnswers.every(([_, val]) => val === "");

                                                return (
                                                    answer.isCorrect ? (
                                                        <p>Correct</p>
                                                    ) : allEmpty ? (
                                                        <p className="digitalSHSATReport__questionOmitted">Omitted</p>
                                                    ) : (
                                                        <p className="digitalSHSATReport__questionIncorrect">Incorrect</p>
                                                    )
                                                );
                                            case 8:
                                                return (answer.isCorrect
                                                    ? <p>Correct</p>
                                                    : answer.studentAnswers.length > 0
                                                    ? <p className="digitalSHSATReport__questionIncorrect">Incorrect</p>
                                                    : <p className="digitalSHSATReport__questionOmitted">Omitted</p>
                                                )
                                            case 9:
                                                return (answer.isCorrect
                                                    ? <p>Correct</p>
                                                    : answer.studentAnswers.length > 0
                                                    ? <p className="digitalSHSATReport__questionIncorrect">Incorrect</p>
                                                    : <p className="digitalSHSATReport__questionOmitted">Omitted</p>
                                                )
                                            case 10:
                                                return answer?.choice?.length > 0? (
                                                JSON.stringify([...answer.choice].sort()) === JSON.stringify([...answer.correctChoice].sort()) ? (
                                                    <p>Correct</p>
                                                ) : (
                                                    <p className="digitalSHSATReport__questionIncorrect">Incorrect</p>
                                                )
                                                ) : (
                                                <p className="digitalSHSATReport__questionOmitted">Omitted</p>
                                                );
                                            case 11:
                                            case 12:
                                                return (answer.isCorrect
                                                    ? <p>Correct</p>
                                                    : answer.studentAnswers
                                                    ? <p className="digitalSHSATReport__questionIncorrect">Incorrect</p>
                                                    : <p className="digitalSHSATReport__questionOmitted">Omitted</p>
                                                )
                                            default:
                                                return "-";
                                            }
                                        })()}
                                    </td>
                                    <td>
                                        <p className={answer.timeSpent > (testData.testModules.length === 1
                                                    ? timePerQuestionOne * 1.5
                                                    : testData.testModules.length === 2
                                                    ? timePerQuestionFull * 1.5
                                                    : 60 * 1.5
                                                )
                                            ?   'digitalSHSATReport__timeSpentBad'
                                            :   answer.timeSpent > (testData.testModules.length === 1
                                                    ? timePerQuestionOne
                                                    : testData.testModules.length === 2
                                                    ? timePerQuestionFull
                                                    : 60
                                                )
                                            ?   'digitalSHSATReport__timeSpentWarning'
                                            :   'digitalSHSATReport__timeSpentGood'
                                        }>
                                            {formatTime(answer.timeSpent)}
                                        </p>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default DigitalSHSATReport;